import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Table, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { sickLeaveApplicationsApiService } from '../../services/apiServices';
import { SickLeaveApplicationResponse, SickLeaveApplicationStatus } from '../../api-client';
import { getSickLeaveApplicationStatusLabel } from '../utils/statusConverters';
import { SmartPagination } from '../../components/SmartPagination';
import 'bootstrap-daterangepicker/daterangepicker.css';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import moment, { Moment } from 'moment';

const SickLeaveApplicationsListPage: React.FC = () => {
  const [applications, setApplications] = useState<SickLeaveApplicationResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Локальные состояния для текстовых полей
  const [userSearchInput, setUserSearchInput] = useState<string>('');

  // Состояния для отображения выбранных диапазонов
  const [startDateRangeLabel, setStartDateRangeLabel] = useState<string>('Любая дата');
  const [endDateRangeLabel, setEndDateRangeLabel] = useState<string>('Любая дата');

  // Извлекаем параметры из URL
  const userSearchFilter = searchParams.get('user_search') || '';
  const statusFilter = searchParams.get('status') || '';
  const startDateFromFilter = searchParams.get('start_date_from') || '';
  const startDateToFilter = searchParams.get('start_date_to') || '';
  const endDateFromFilter = searchParams.get('end_date_from') || '';
  const endDateToFilter = searchParams.get('end_date_to') || '';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const limit = 10;

  // Ref для предотвращения дублирования запросов
  const requestParamsRef = useRef<string | null>(null);
  const isRequestInProgressRef = useRef<boolean>(false);

  // Обновление меток дат при изменении фильтров
  useEffect(() => {
    setUserSearchInput(userSearchFilter);

    if (startDateFromFilter && startDateToFilter) {
      setStartDateRangeLabel(
        `${moment(startDateFromFilter).format('DD.MM.YYYY')} - ${moment(startDateToFilter).format('DD.MM.YYYY')}`
      );
    } else if (startDateFromFilter) {
      setStartDateRangeLabel(`от ${moment(startDateFromFilter).format('DD.MM.YYYY')}`);
    } else if (startDateToFilter) {
      setStartDateRangeLabel(`до ${moment(startDateToFilter).format('DD.MM.YYYY')}`);
    } else {
      setStartDateRangeLabel('Любая дата');
    }

    if (endDateFromFilter && endDateToFilter) {
      setEndDateRangeLabel(
        `${moment(endDateFromFilter).format('DD.MM.YYYY')} - ${moment(endDateToFilter).format('DD.MM.YYYY')}`
      );
    } else if (endDateFromFilter) {
      setEndDateRangeLabel(`от ${moment(endDateFromFilter).format('DD.MM.YYYY')}`);
    } else if (endDateToFilter) {
      setEndDateRangeLabel(`до ${moment(endDateToFilter).format('DD.MM.YYYY')}`);
    } else {
      setEndDateRangeLabel('Любая дата');
    }
  }, [
    startDateFromFilter,
    startDateToFilter,
    endDateFromFilter,
    endDateToFilter,
    userSearchFilter,
  ]);

  // Загрузка заявок
  useEffect(() => {
    const paramsString = JSON.stringify({
      user_search: userSearchFilter,
      status: statusFilter,
      start_date_from: startDateFromFilter,
      start_date_to: startDateToFilter,
      end_date_from: endDateFromFilter,
      end_date_to: endDateToFilter,
      page: currentPage,
    });

    if (requestParamsRef.current === paramsString) {
      return;
    }

    if (isRequestInProgressRef.current) {
      return;
    }

    requestParamsRef.current = paramsString;
    isRequestInProgressRef.current = true;
    setLoading(true);
    setError(null);

    const loadApplications = async () => {
      try {
        const response = await sickLeaveApplicationsApiService.getApplications(
          limit,
          (currentPage - 1) * limit,
          statusFilter || undefined,
          undefined,
          userSearchFilter || undefined,
          startDateFromFilter || undefined,
          startDateToFilter || undefined,
          endDateFromFilter || undefined,
          endDateToFilter || undefined
        );

        setApplications(response.data || []);
      } catch (err: any) {
        console.error('Ошибка при загрузке заявок на больничный:', err);
        setError(err.message || 'Ошибка при загрузке заявок');
      } finally {
        isRequestInProgressRef.current = false;
        setLoading(false);
      }
    };

    loadApplications();

    return () => {
      isRequestInProgressRef.current = false;
    };
  }, [
    userSearchFilter,
    statusFilter,
    currentPage,
    limit,
    startDateFromFilter,
    startDateToFilter,
    endDateFromFilter,
    endDateToFilter,
  ]);

  // Обновление параметров URL
  const updateUrlParams = useCallback(
    (newParams: Partial<Record<string, string>>) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);

          Object.entries(newParams).forEach(([key, value]) => {
            if (value === '' || value === null || value === undefined) {
              params.delete(key);
            } else {
              params.set(key, value);
            }
          });

          if (!('page' in newParams)) {
            params.set('page', '1');
          }

          return params;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  // Обработчики
  const handleUserSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        updateUrlParams({ user_search: userSearchInput, page: '1' });
      }
    },
    [userSearchInput, updateUrlParams]
  );

  const handleStatusChange = useCallback(
    (value: string) => {
      updateUrlParams({ status: value, page: '1' });
    },
    [updateUrlParams]
  );

  // Обработчик изменения диапазона дат начала
  const handleStartDateCallback = useCallback(
    (start: Moment, end: Moment, label: string | undefined) => {
      const startStr = start.isValid() ? start.format('YYYY-MM-DD') : '';
      const endStr = end.isValid() ? end.format('YYYY-MM-DD') : '';

      if (label === 'Любая дата') {
        updateUrlParams({ start_date_from: '', start_date_to: '', page: '1' });
      } else {
        updateUrlParams({
          start_date_from: startStr,
          start_date_to: endStr,
          page: '1',
        });
      }
    },
    [updateUrlParams]
  );

  // Обработчик изменения диапазона дат окончания
  const handleEndDateCallback = useCallback(
    (start: Moment, end: Moment, label: string | undefined) => {
      const startStr = start.isValid() ? start.format('YYYY-MM-DD') : '';
      const endStr = end.isValid() ? end.format('YYYY-MM-DD') : '';

      if (label === 'Любая дата') {
        updateUrlParams({ end_date_from: '', end_date_to: '', page: '1' });
      } else {
        updateUrlParams({
          end_date_from: startStr,
          end_date_to: endStr,
          page: '1',
        });
      }
    },
    [updateUrlParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('page', newPage.toString());
        return params;
      });
    },
    [setSearchParams]
  );

  const handleResetFilters = useCallback(() => {
    setUserSearchInput('');
    setStartDateRangeLabel('Любая дата');
    setEndDateRangeLabel('Любая дата');
    updateUrlParams({
      user_search: '',
      status: '',
      start_date_from: '',
      start_date_to: '',
      end_date_from: '',
      end_date_to: '',
      page: '1',
    });
  }, [updateUrlParams]);

  const getStatusBadgeVariant = (status: SickLeaveApplicationStatus) => {
    switch (status) {
      case SickLeaveApplicationStatus.PENDING:
        return 'warning';
      case SickLeaveApplicationStatus.ACTIVE:
        return 'info';
      case SickLeaveApplicationStatus.COMPLETED:
        return 'success';
      case SickLeaveApplicationStatus.CANCELED:
        return 'danger';
      default:
        return 'secondary';
    }
  };

  // Настройки для DateRangePicker
  const dateRangePickerSettings = {
    locale: {
      format: 'DD.MM.YYYY',
      separator: ' - ',
      applyLabel: 'Применить',
      cancelLabel: 'Отмена',
      fromLabel: 'от',
      toLabel: 'до',
      customRangeLabel: 'Выбрать диапазон',
      weekLabel: 'Н',
      daysOfWeek: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
      monthNames: [
        'Январь',
        'Февраль',
        'Март',
        'Апрель',
        'Май',
        'Июнь',
        'Июль',
        'Август',
        'Сентябрь',
        'Октябрь',
        'Ноябрь',
        'Декабрь',
      ],
      firstDay: 1,
    },
    ranges: {
      'Любая дата': [],
      Сегодня: [moment(), moment()],
      'Последние 7 дней': [moment().subtract(6, 'days'), moment()],
      'Последние 30 дней': [moment().subtract(29, 'days'), moment()],
      'Этот месяц': [moment().startOf('month'), moment().endOf('month')],
      'Прошлый месяц': [
        moment().subtract(1, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ],
    },
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Заявки на больничный</h2>
          </div>

          {/* Фильтры */}
          <div className="row mb-4">
            <div className="col-md-3 mb-2">
              <Form.Label>Фильтр по пользователю (email, имя пользователя или ФИО)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Email, имя пользователя или ФИО"
                value={userSearchInput}
                onChange={(e) => setUserSearchInput(e.target.value)}
                onKeyDown={handleUserSearchKeyDown}
              />
            </div>
            <div className="col-md-3 mb-2">
              <Form.Label>Статус</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <option value="">Все</option>
                <option value="pending">Ожидает обработки</option>
                <option value="active">Активен</option>
                <option value="completed">Завершен</option>
                <option value="canceled">Отменен</option>
              </Form.Select>
            </div>
            <div className="col-md-3 mb-2">
              <Form.Label>Дата начала</Form.Label>
              <DateRangePicker
                initialSettings={{
                  ...dateRangePickerSettings,
                  startDate: startDateFromFilter ? moment(startDateFromFilter) : moment(),
                  endDate: startDateToFilter ? moment(startDateToFilter) : moment(),
                }}
                onCallback={handleStartDateCallback}
              >
                <Form.Control
                  type="text"
                  value={startDateRangeLabel}
                  readOnly
                  style={{ cursor: 'pointer' }}
                />
              </DateRangePicker>
            </div>
            <div className="col-md-3 mb-2">
              <Form.Label>Дата окончания</Form.Label>
              <DateRangePicker
                initialSettings={{
                  ...dateRangePickerSettings,
                  startDate: endDateFromFilter ? moment(endDateFromFilter) : moment(),
                  endDate: endDateToFilter ? moment(endDateToFilter) : moment(),
                }}
                onCallback={handleEndDateCallback}
              >
                <Form.Control
                  type="text"
                  value={endDateRangeLabel}
                  readOnly
                  style={{ cursor: 'pointer' }}
                />
              </DateRangePicker>
            </div>
            <div className="col-md-2 mb-2 d-flex align-items-end">
              <Button variant="outline-secondary" className="w-100" onClick={handleResetFilters}>
                Сбросить
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" />
              <p className="mt-2">Загрузка заявок...</p>
            </div>
          ) : (
            <>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Пользователь</th>
                    <th>Дата начала</th>
                    <th>Дата окончания</th>
                    <th>Статус</th>
                    <th>Дата создания</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {applications && applications.length > 0 ? (
                    applications.map((app) => {
                      const userEmail = app.user?.email || '—';
                      const userName = app.user?.username || '—';
                      const userFullName = app.user?.profile?.full_name;
                      return (
                        <tr key={app.id}>
                          <td>{app.id}</td>
                          <td>
                            {userFullName && (
                              <div>
                                <strong>{userFullName}</strong>
                              </div>
                            )}
                            <div>
                              <strong>{userName}</strong>
                            </div>
                            <small className="text-muted">{userEmail}</small>
                          </td>
                          <td>{new Date(app.start_date).toLocaleDateString()}</td>
                          <td>{new Date(app.end_date).toLocaleDateString()}</td>
                          <td>
                            <Badge bg={getStatusBadgeVariant(app.status)}>
                              {getSickLeaveApplicationStatusLabel(app.status)}
                            </Badge>
                          </td>
                          <td>{new Date(app.created_at).toLocaleDateString()}</td>
                          <td>
                            <Link to={`/sick-leave/applications/${app.id}`}>
                              <Button variant="outline-primary" size="sm">
                                Подробнее
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">
                        Заявки не найдены
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {/* Пагинация */}
              <SmartPagination
                currentPage={currentPage}
                limit={limit}
                currentItemsCount={applications.length}
                onPageChange={handlePageChange}
                siblingCount={1}
              />
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default SickLeaveApplicationsListPage;
